import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXS, LabelS } from '@sorare/core/src/atoms/typography';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import Dialog from '@sorare/core/src/components/dialog';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { cart } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import {
  PlayerDetails as PlayerDetailsType,
  PlayerDrawer,
} from 'components/player/PlayerDrawer';

import { MainCartItems } from '../MainCartItems';
import { SavedForLaterItems } from '../SavedForLaterItems';
import type {
  CheckoutQuery,
  CheckoutQueryVariables,
} from './__generated__/index.graphql';

const Layout = styled(Horizontal).attrs({
  gap: 0,
})`
  height: 100%;
`;

const Cart = styled(Vertical).attrs({
  gap: 0,
})`
  width: 100%;
  height: 100%;
  padding: var(--triple-unit) var(--double-unit);
  @media ${laptopAndAbove} {
    border-right: 1px solid var(--c-nd-150);
  }
  overflow-y: auto;
  align-items: center;
`;

const CartContainer = styled(Vertical).attrs({
  gap: 3,
})`
  width: 100%;
  max-width: 850px;
`;

const PaymentScrollablePlaceholder = styled.div`
  height: 1000px;
`;
const Payment = styled.div`
  display: none;
  @media ${laptopAndAbove} {
    position: static;
    display: block;
    width: 437px;
    flex-shrink: 0;
    overflow-y: auto;
    height: 100%;
  }
`;
const Footer = styled(Horizontal).attrs({
  gap: 3,
})`
  ${regularMaterial}
  padding: var(--double-unit) var(--triple-unit);
  @media ${laptopAndAbove} {
    display: none;
  }
`;

const DIALOG_KEY = DialogKey.checkout;

const CHECKOUT_QUERY = gql`
  query CheckoutQuery($sport: Sport!) {
    currentUser {
      slug
      myCart(sport: $sport) {
        id
        mainCartItems {
          id
          ...MainCartItems_cartItem
        }
        savedForLaterCartItems {
          id
          ...SavedForLaterItems_cartItem
        }
      }
    }
  }
  ${MainCartItems.fragments.cartItem}
  ${SavedForLaterItems.fragments.cartItem}
` as TypedDocumentNode<CheckoutQuery, CheckoutQueryVariables>;

interface Props {
  PlayerDetails: PlayerDetailsType;
}
export const Checkout = ({ PlayerDetails }: Props) => {
  const {
    flags: { useBulkBuy = false },
  } = useFeatureFlags();
  const [playerSlug, setPlayerSlug] = useState<string | null>(null);
  const [openPayment, setOpenPayment] = useState<boolean>(false);
  const closeDialog = useCloseDialog();
  const onClose = () => closeDialog(DIALOG_KEY);

  const { data } = useQuery<CheckoutQuery, CheckoutQueryVariables>(
    CHECKOUT_QUERY,
    {
      variables: {
        sport: Sport.FOOTBALL,
      },
      skip: !useBulkBuy,
    }
  );
  const mainCartItems = data?.currentUser?.myCart?.mainCartItems || [];
  const savedForLaterItems =
    data?.currentUser?.myCart?.savedForLaterCartItems || [];
  if (!useBulkBuy) return null;
  return (
    <>
      <Dialog
        open
        fullScreen
        onClose={onClose}
        title={
          <HeadlineXS className="text-center">
            <FormattedMessage {...cart.cart} />
          </HeadlineXS>
        }
        withDivider
        footer={
          <Footer>
            <Vertical>
              <LabelS color="var(--c-green-600)">
                <FormattedMessage {...cart.creditApplied} />
              </LabelS>
              <AmountWithConversion monetaryAmount={zeroMonetaryAmount} />
            </Vertical>
            <Button
              onClick={() => setOpenPayment(true)}
              color="primary"
              fullWidth
              size="medium"
            >
              <FormattedMessage {...cart.checkout} />
            </Button>
          </Footer>
        }
      >
        <Layout>
          <Cart>
            <CartContainer>
              <MainCartItems cartItems={mainCartItems} />
              <SavedForLaterItems
                cartItems={savedForLaterItems}
                onStatsClick={c => {
                  setPlayerSlug(c);
                }}
              />
            </CartContainer>
          </Cart>

          <Payment>
            <PaymentScrollablePlaceholder />
          </Payment>
        </Layout>
      </Dialog>
      <Dialog
        open={openPayment}
        onBack={() => setOpenPayment(false)}
        fullScreen
        title={
          <HeadlineXS className="text-center">
            <FormattedMessage {...cart.checkout} />
          </HeadlineXS>
        }
        withDivider
      >
        <PaymentScrollablePlaceholder />
      </Dialog>
      <PlayerDrawer
        cardOpened={
          playerSlug ? { cardSlug: '', playerSlug: playerSlug || '' } : null
        }
        onClose={() => setPlayerSlug(null)}
        PlayerDetails={PlayerDetails}
      />
    </>
  );
};

export default Checkout;
